import { TeamType } from '@modules/Auth/models/Auth';
import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';

import { ClientPackagingRulesets, ClientRules, Clients, CreateEditClient, ViewClient } from '@modules/Clients';
import {
  ClientRulesRedirectFromCreate,
  ClientRulesRedirectFromEdit,
  ClientRulesRedirectFromView,
} from '@modules/Clients/ClientRules/ClientRulesLegacyRedirects';
import {
  CourierFilesList,
  CourierFileView,
  MismatchesList,
  PayoutItemsList,
  PayoutsList,
  PayoutView,
} from '@modules/CODPayments';
import { CourierInvoicesList, CourierInvoiceView, CourierSettlementView } from '@modules/CourierInvoices';
import { ViewCRM } from '@modules/CRM/Details';
import { CRM } from '@modules/CRM/List';
import { EligibleStorage } from '@modules/Invoices/EligibleStorage';
import { Invoices as InvoicesList } from '@modules/Invoices/Invoices/List';
import { InvoicesView } from '@modules/Invoices/Invoices/View';
import { InvoicesOrders as InvoicesOrdersList } from '@modules/Invoices/InvoicesOrders/List';
import { MinimumFees } from '@modules/Invoices/MinimumFees';
import { PreInvoices } from '@modules/Invoices/PreInvoices/List';
import { ViewPreInvoices } from '@modules/Invoices/PreInvoices/View';
import { MerchandiseV3 } from '@modules/MerchandiseV3';
import { Orders } from '@modules/Orders/List';
import { OrdersShipped } from '@modules/OrdersShipped';
import { PackagingRulesets } from '@modules/Packaging/packagingRulesets';
import { Popups } from '@modules/Popups';
import { Products } from '@modules/Products/List';
import { CreateRate, RateDetail, RatesList } from '@modules/Rates';
import { ProductsWithoutStock } from '@modules/Reports/ProductsWithoutStock';
import { Returned } from '@modules/Returned';
import { Locate, Receive } from '@modules/Returned/Receive';
import { ReusedList, ReusedReceive } from '@modules/Reused';

// Nueva implementación con Clean Architecture
import { Carts } from '@modules/Carts';
import Affiliates from '@modules/configuration/Affiliates/Affiliates';
import CoverageZones from '@modules/configuration/CoverageZones';
import { General } from '@modules/configuration/General';
import { IntegratedMessaging } from '@modules/configuration/IntegratedMessaging';
import { NotificationSettings } from '@modules/configuration/NotificationSettings';
import { Printers } from '@modules/configuration/Printers';
import { Profile } from '@modules/configuration/Profile';
import { Roles } from '@modules/configuration/Roles';
import { ShipmentStatusMapping } from '@modules/configuration/ShipmentStatusMapping/ShipmentStatusMapping';
import {
  ShippingRulePresetEditor,
  ShippingRulePresets,
  ShippingRulePresetsHistory,
} from '@modules/configuration/ShippingRulePresets';
import { Suscription } from '@modules/configuration/Suscription';
import VerifyNewEmailPage from '@modules/configuration/Users/pages/VerifyNewEmailPage';
import Users from '@modules/configuration/Users/Users';
import { Home } from '@modules/Home';
import { Locations } from '@modules/Locations/Locations';
import { Packs } from '@modules/Packs';
import PendingOrders from '@modules/PendingOrders/PendingOrders';
// import { Merchandise } from '@modules/Merchandise'; // OCULTO - usar MerchandiseV3
import { DataLoad } from '@modules/Accounting/DataLoad';
import { BatchDetail } from '@modules/Batches/Detail';
import { Batches } from '@modules/Batches/List';
import { Integrations } from '@modules/configuration/Integrations';
import { ProductCategories } from '@modules/configuration/ProductCategories';
import { WebhookDeliveriesView, Webhooks } from '@modules/configuration/Webhooks';
import { ExtraWorkOrderDetail, ExtraWorkOrdersList } from '@modules/ExtraWorkOrders';
import { Detail } from '@modules/LocationOrders/Detail';
import { LocationOrders } from '@modules/LocationOrders/List';
import { Incidents } from '@modules/OrderIncidents/Incidents/List';
import { Mappings } from '@modules/OrderIncidents/Mappings/List';
import { OrderPreparation } from '@modules/OrderPreparation/List';
import { Manifests } from '@modules/OrderPreparation/Manifests';
import { MassivePacking } from '@modules/OrderPreparation/MassivePacking';
import { Packing } from '@modules/OrderPreparation/Packing';
import { PackingV2 } from '@modules/OrderPreparation/PackingV2';
import { Picking } from '@modules/OrderPreparation/Picking';
import { CreateEditOrders } from '@modules/Orders/CreateEditOrders';
import { ViewOrders } from '@modules/Orders/ViewOrders';
import { MaterialAlmacen, Packaging } from '@modules/Packaging';
import { PackingStations } from '@modules/Packaging/PackingStations/List';
import { CreateEdit as ProductCreateEdit } from '@modules/Products/CreateEdit';
import { ReceptionDetailPage, Receptions } from '@modules/Receptions';
import { Relocations } from '@modules/Relocations/List';
import { RelocationProcess } from '@modules/Relocations/Process';
import { Inventory } from '@modules/Reports/Inventory';
import { LegacyDashboard } from '@modules/Reports/LegacyDashboard';
import { LocationMovements as LocationMovementsV2 } from '@modules/Reports/LocationMovements';
import { Logistics } from '@modules/Reports/Logistics';
import { Marketing } from '@modules/Reports/Marketing';
import OrdersWithoutWeight from '@modules/Reports/OrdersWithoutWeight';
import {
  AdSpendPage,
  DefaultShippingCostsPage,
  ProductCostsPage,
  Profitability,
  ProfitabilityOrderDetailPage,
} from '@modules/Reports/Profitability';
import { ReadyToPrepareByHour } from '@modules/Reports/ReadyToPrepareByHour';
import { Refunds } from '@modules/Reports/Refunds';
import { StockMovements as StockMovementsV2 } from '@modules/Reports/StockMovements';
import { ThreePlExcelReports } from '@modules/Reports/ThreePlExcelReports';
import { Tracking } from '@modules/Reports/Tracking';
import ImportHistory from '@modules/Shops/ImportHistory/ImportHistory';
import { Shops } from '@modules/Shops/List';
import { AccountingSyncList } from '@modules/Wallets/AccountingSync';
import { BankAccounts } from '@modules/Wallets/BankAccounts';
import { Wallets } from '@modules/Wallets/List';
import { Topups } from '@modules/Wallets/Topups';
import { Wallet } from '@modules/Wallets/Wallet';
import { Withdrawals } from '@modules/Wallets/Withdrawals';
import { WarehouseMapView } from '@modules/WarehouseMap/components/display/index';
import { Main as WarehouseMapMain } from '@modules/WarehouseMap/components/containers/Main';
import { WithdrawalPaymentFiles } from '@modules/Wallets/WithdrawalPaymentFiles';

export interface ProtectedRouteConfig {
  path: string;
  element: ReactNode;
  teamTypes?: TeamType[];
  permissions?: string[];
  requireAll?: boolean;
  requireRoleAdmin?: boolean;
  requireCRMEnabled?: boolean; // Si true, solo permitir acceso si crm.enabled es true
  requireWalletVisible?: boolean;
  requireProfitabilityAccess?: boolean;
}

export const protectedRoutes: ProtectedRouteConfig[] = [
  {
    path: '',
    element: <Navigate to="/home" replace />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'home',
    element: <Home />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'products',
    element: <Products />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.view'],
  },
  {
    path: 'orders/crm',
    element: <CRM />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders-crm.view'],
    requireCRMEnabled: true, // Solo permitir acceso si CRM está habilitado
  },
  {
    path: 'orders/crm/view/:orderId',
    element: <ViewCRM />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders-crm.view'],
    requireCRMEnabled: true, // Solo permitir acceso si CRM está habilitado
  },
  {
    path: 'incidents',
    element: <Incidents />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    // permissions: ['incidents.view'], // Comentado temporalmente
  },
  {
    path: 'configuration/incident-mappings',
    element: <Mappings />,
    teamTypes: ['tenant_3pl'],
    permissions: ['incidents.mapping'],
  },
  {
    path: 'products-without-stock',
    element: <ProductsWithoutStock />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['product-without-stock-report.view'],
  },
  {
    path: 'products/create',
    element: <ProductCreateEdit />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.create'],
  },
  {
    path: 'products/edit/:id',
    element: <ProductCreateEdit />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['products.edit'],
  },
  {
    path: 'orders/create',
    element: <CreateEditOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.create'],
  },
  {
    path: 'orders/edit/:id',
    element: <CreateEditOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.edit'],
  },
  {
    path: 'orders/view/:orderId',
    element: <ViewOrders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.view'],
  },
  {
    path: 'packs',
    element: <Packs />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['packs.view'],
  },
  {
    path: 'packaging',
    element: <Packaging />,
    teamTypes: ['tenant', 'tenant_3pl'],
  },
  {
    path: 'packaging/warehouse-material',
    element: <MaterialAlmacen />,
    teamTypes: ['tenant', 'tenant_3pl'],
  },
  {
    path: 'packaging/packing-stations',
    element: <PackingStations />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'packaging/packaging-rulesets',
    element: <PackagingRulesets />,
    teamTypes: ['tenant'],
    permissions: ['configuration.view'],
  },
  {
    path: 'clients/:clientId/packaging-rulesets',
    element: <ClientPackagingRulesets />,
    teamTypes: ['tenant_3pl'],
    permissions: ['configuration.view'],
  },
  {
    path: 'returned',
    element: <Returned />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'returned/:id/receive',
    element: <Receive />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'returned/:id/locate',
    element: <Locate />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'reused',
    element: <ReusedList />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'reused/:id/receive',
    element: <ReusedReceive />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['returns.view'],
  },
  {
    path: 'locations',
    element: <Locations />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['locations.view'],
  },
  {
    path: 'carts',
    element: <Carts />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'pending-orders',
    element: <PendingOrders />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['orders.create-preparation'],
  },
  {
    path: 'wallets',
    element: <Wallets />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['wallets.view'],
  },
  {
    path: 'wallets/:id',
    element: <Wallet />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['wallets.view'],
  },
  {
    path: 'wallets/withdrawals',
    element: <Withdrawals />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['withdrawals.view'],
  },
  {
    path: 'withdrawal-payment-files',
    element: <WithdrawalPaymentFiles />,
    teamTypes: ['tenant_3pl'],
    permissions: ['withdrawals.view'],
  },
  {
    path: 'wallets/bank-accounts',
    element: <BankAccounts />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['bank-accounts.view'],
  },
  {
    path: 'wallets/topups',
    element: <Topups />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['topups.view'],
  },
  {
    path: 'configuration/printers',
    element: <Printers />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['printers.view'],
  },
  {
    path: 'configuration/general',
    element: <General />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['configuration.view'],
  },
  {
    path: 'configuration/couriers',
    element: <IntegratedMessaging />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['shipping.couriers.view'],
  },
  {
    path: '/configuration/profile',
    element: <Profile />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: '/configuration/notifications',
    element: <NotificationSettings />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'stock-movements',
    element: <StockMovementsV2 />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['stock-movements.view'],
  },
  {
    path: 'location-movements',
    element: <LocationMovementsV2 />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['stock-movements.view'],
  },
  {
    path: 'reports/logistics',
    element: <Logistics />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'reports/logistic-pivot',
    element: <Navigate to="/reports/logistics#logistic-pivot-dynamic" replace />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
  },
  {
    path: 'reports/profitability/orders/:orderId',
    element: <ProfitabilityOrderDetailPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/ad-spend',
    element: <AdSpendPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/shipping-costs',
    element: <DefaultShippingCostsPage />,
    teamTypes: ['tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability/product-costs',
    element: <ProductCostsPage />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'reports/profitability',
    element: <Profitability />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
    requireProfitabilityAccess: true,
  },
  {
    path: 'configuration/tracking-monitoring',
    element: <Tracking />,
    teamTypes: ['tenant_3pl'],
    permissions: ['tracking-monitoring.view'],
  },
  {
    path: 'reports/inventory',
    element: <Inventory />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['inventory-report.view'],
  },
  {
    path: 'reports/marketing',
    element: <Marketing />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['logistics-report.view'],
  },
  {
    path: 'reports/orders-without-weight',
    element: <OrdersWithoutWeight />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/wallet-accounting-sync',
    element: <AccountingSyncList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['wallets.view'],
    requireWalletVisible: true,
  },
  {
    path: 'reports/warehouse/ready-to-prepare-by-hour',
    element: <ReadyToPrepareByHour />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/3pl/excel',
    element: <ThreePlExcelReports />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'reports/3pl/legacy-dashboard',
    element: <LegacyDashboard />,
    teamTypes: ['tenant_3pl'],
    permissions: ['reports.view'],
  },
  {
    path: 'accounting/data-load',
    element: <DataLoad />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'reports/cod-payments',
    element: <Refunds />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['reports.view'],
  },
  {
    path: 'merchandise/*',
    element: <MerchandiseV3 />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['merchandises.view'],
  },
  {
    path: 'receptions',
    element: <Receptions />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'receptions/:id',
    element: <ReceptionDetailPage />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['receptions.view'],
  },
  {
    path: 'orders',
    element: <Orders />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['orders.view'],
  },
  {
    path: 'invoices/eligible-shipments',
    element: <InvoicesOrdersList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/eligible-storage',
    element: <EligibleStorage />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/minimum_fees',
    element: <MinimumFees />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/pre-invoices',
    element: <PreInvoices />,
    teamTypes: ['tenant_3pl'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices',
    element: <InvoicesList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/view/:preInvoiceId',
    element: <InvoicesView />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/pre-invoices/view/:preInvoiceId',
    element: <ViewPreInvoices />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['invoices.view'],
  },
  {
    path: 'invoices/rates',
    element: <RatesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.view'],
  },
  {
    path: 'invoices/rates/:id',
    element: <RateDetail />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.view'],
  },
  {
    path: 'invoices/rates/new',
    element: <CreateRate />,
    teamTypes: ['tenant_3pl'],
    permissions: ['rates.manage'],
  },
  {
    path: 'courier-invoices',
    element: <CourierInvoicesList />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'courier-invoices/:id',
    element: <CourierInvoiceView />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'courier-settlements/:id',
    element: <CourierSettlementView />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'orders-shipped',
    element: <OrdersShipped />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shipped-orders.view'],
  },
  {
    path: 'batches',
    element: <Batches />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['products.view'],
  },
  {
    path: 'batches/:id',
    element: <BatchDetail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['products.view'],
  },
  {
    path: 'extra-work-orders',
    element: <ExtraWorkOrdersList />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['extra-work-orders.view'],
  },
  {
    path: 'extra-work-orders/:id',
    element: <ExtraWorkOrderDetail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['extra-work-orders.view'],
  },
  {
    path: 'clients',
    element: <Clients />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/create',
    element: <CreateEditClient />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:id',
    element: <ViewClient />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:clientId/rules',
    element: <ClientRules />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'clients/:clientId/rules/create',
    element: <ClientRulesRedirectFromCreate />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.create'],
  },
  {
    path: 'clients/:clientId/rules/:id/edit',
    element: <ClientRulesRedirectFromEdit />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.edit'],
  },
  {
    path: 'clients/:clientId/rules/:id',
    element: <ClientRulesRedirectFromView />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'configuration/roles',
    element: <Roles />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    requireRoleAdmin: true,
  },
  {
    path: 'configuration/users',
    element: <Users />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    requireRoleAdmin: true,
  },
  {
    path: 'shops',
    element: <Shops />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shops.view'],
  },
  {
    path: 'shops/import-history/:shopId?',
    element: <ImportHistory />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
    permissions: ['shops.view'],
  },
  {
    path: 'configuration/suscription',
    element: <Suscription />,
    teamTypes: ['tenant'],
    permissions: ['configuration.view'],
  },
  {
    path: 'configuration/affiliates',
    element: <Affiliates />,
    teamTypes: ['tenant'],
    permissions: ['configuration.view'],
  },
  {
    path: 'configuration/coverage-zones',
    element: <CoverageZones />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['shipping.coverage.view'],
  },
  {
    path: 'configuration/popups/*',
    element: <Popups />,
    teamTypes: ['tenant_3pl'],
    permissions: ['popups.view'],
  },
  {
    path: 'configuration/shipping-rule-presets/create',
    element: <ShippingRulePresetEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.create'],
  },
  {
    path: 'configuration/shipping-rule-presets/:presetId/edit',
    element: <ShippingRulePresetEditor />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.edit'],
  },
  {
    path: 'configuration/shipping-rule-presets/history',
    element: <ShippingRulePresetsHistory />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.view'],
  },
  {
    path: 'configuration/shipping-rule-presets',
    element: <ShippingRulePresets />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipping.rules.view'],
  },
  {
    path: 'configuration/shipment-status-mapping',
    element: <ShipmentStatusMapping />,
    teamTypes: ['tenant_3pl'],
    permissions: ['shipment_status_mappings.view'],
  },
  {
    path: 'configuration/product-categories',
    element: <ProductCategories />,
    teamTypes: ['tenant_3pl'],
    permissions: ['categories.view'],
  },
  {
    path: 'configuration/integrations',
    element: <Integrations />,
    teamTypes: ['tenant_external'],
    permissions: ['integrations.view'],
  },
  {
    path: 'configuration/webhooks',
    element: <Webhooks />,
    teamTypes: ['tenant_external'],
    permissions: ['webhooks.view'],
  },
  {
    path: 'configuration/webhooks/:id',
    element: <WebhookDeliveriesView />,
    teamTypes: ['tenant_external'],
    permissions: ['webhooks.view'],
  },
  {
    path: 'order-preparation',
    element: <OrderPreparation />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.view'],
  },
  {
    path: 'order-preparation/manifests',
    element: <Manifests />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.view'],
  },
  {
    path: 'order-preparation/picking/:id',
    element: <Picking />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.picking'],
  },
  {
    path: 'order-preparation/packingv1/:id',
    element: <Packing />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'order-preparation/packing/:id',
    element: <PackingV2 />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'order-preparation/massive-packing/:id',
    element: <MassivePacking />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['preparations.packing'],
  },
  {
    path: 'location-orders',
    element: <LocationOrders />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['location-orders.view'],
  },
  {
    path: 'location-orders/:id',
    element: <Detail />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['location-orders.view'],
  },
  {
    path: 'relocations',
    element: <Relocations />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'relocations/new',
    element: <RelocationProcess />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'relocations/:id',
    element: <RelocationProcess />,
    teamTypes: ['tenant', 'tenant_3pl'],
    permissions: ['carts.view'],
  },
  {
    path: 'cod-payments/payouts',
    element: <PayoutsList />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cod-payments.payouts.view'],
  },
  {
    path: 'cod-payments/payouts/:id',
    element: <PayoutView />,
    teamTypes: ['tenant_3pl', 'tenant_external'],
    permissions: ['cod-payments.payouts.view'],
  },
  {
    path: 'cod-payments/payout-items',
    element: <PayoutItemsList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.payments.view'],
  },
  {
    path: 'cod-payments/courier-files',
    element: <CourierFilesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.files.view'],
  },
  {
    path: 'cod-payments/courier-files/:id',
    element: <CourierFileView />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.files.view'],
  },
  {
    path: 'cod-payments/mismatches',
    element: <MismatchesList />,
    teamTypes: ['tenant_3pl'],
    permissions: ['cod-payments.ledger.view'],
  },
  {
    path: 'verify-new-email',
    element: <VerifyNewEmailPage />,
    teamTypes: ['tenant', 'tenant_3pl', 'tenant_external'],
  },
  {
    path: 'warehouses',
    element: <WarehouseMapMain />,
    teamTypes: ['tenant_3pl'],
  },
  {
    path: 'warehouses/:id',
    element: <WarehouseMapView />,
    teamTypes: ['tenant_3pl'],
  },
];
